import type { ForwardedRef } from 'react';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Placeholder from '@tiptap/extension-placeholder';
import type { JSONContent } from '@tiptap/react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import type { SuggestionKeyDownProps, SuggestionProps } from '@tiptap/suggestion';

import { inferContextType } from '../../resources';
import type { ResourceOrQuery } from '../../resources';

import type { MentionStorage } from './MentionExtension';
import { VegaMention } from './MentionExtension';
import { MentionSuggestionPopover } from './MentionSuggestionPopover';
import { getContextLabel } from './mentionUtils';

import { vegaEditorInput, vegaEditorPlaceholder } from './mentions.css';

function getMentionStorage(editor: { storage: any }): MentionStorage {
  return editor.storage.mention as MentionStorage;
}

export interface VegaMentionsInputHandle {
  focus: () => void;
  clear: () => void;
  /** Replaces the editor content with an HTML string. Tiptap parses the markup directly, so callers must pass valid HTML (e.g. paragraphs wrapped in `<p>` tags) rather than raw plain text. */
  setContent: (html: string) => void;
  getMentionResources: () => ResourceOrQuery[];
  /** Insert an inline @-mention chip programmatically (used to migrate items opened via "Ask Vega"). */
  insertMention: (resource: ResourceOrQuery) => void;
}

export interface VegaMentionsInputProps {
  placeholder?: string;
  disabled?: boolean;
  /** Optional initial text to seed the editor on mount. */
  initialContent?: string;
  onTextChange: (text: string) => void;
  onSubmit: () => void;
}

interface SuggestionState {
  isOpen: boolean;
  position: { top: number; left: number } | null;
  query: string;
  command: ((attrs: { id: string; label: string; resourceType: string }) => void) | null;
}

export function extractPlainText(doc: JSONContent): string {
  let result = '';
  function walk(node: JSONContent) {
    if (node.type === 'text' && node.text) {
      result += node.text;
    } else if (node.type === 'mention' && node.attrs) {
      const { id, label, resourceType } = node.attrs;
      result += `@[${label}](${resourceType}:${id})`;
    } else if (node.content) {
      node.content.forEach(walk);
    }
  }
  doc.content?.forEach((node, index) => {
    if (index > 0 && node.type === 'paragraph') {
      result += '\n';
    }
    walk(node);
  });
  return result.trim();
}

function getMentionKeys(doc: JSONContent): Set<string> {
  const keys = new Set<string>();
  function walk(node: JSONContent) {
    if (node.type === 'mention' && node.attrs?.id) {
      const rt = node.attrs.resourceType || 'Unknown';
      keys.add(`${rt}:${node.attrs.id}`);
    }
    node.content?.forEach(walk);
  }
  doc.content?.forEach(walk);
  return keys;
}

function mentionKeyForResource(r: { id: string; type?: string }): string {
  return `${r.type || 'Unknown'}:${r.id}`;
}

export const VegaMentionsInput = forwardRef(
  (
    { placeholder, disabled = false, initialContent = '', onTextChange, onSubmit }: VegaMentionsInputProps,
    ref: ForwardedRef<VegaMentionsInputHandle>,
  ) => {
    const onSubmitRef = useRef(onSubmit);
    onSubmitRef.current = onSubmit;
    const onTextChangeRef = useRef(onTextChange);
    onTextChangeRef.current = onTextChange;
    const placeholderRef = useRef(placeholder || 'Ask a question...');
    placeholderRef.current = placeholder || 'Ask a question...';

    const [suggestionState, setSuggestionState] = useState<SuggestionState>({
      isOpen: false,
      position: null,
      query: '',
      command: null,
    });
    const suggestionRangeRef = useRef<{ from: number; to: number } | null>(null);

    const closeSuggestion = useCallback(() => {
      suggestionRangeRef.current = null;
      setSuggestionState((prev) => ({ ...prev, isOpen: false }));
    }, []);
    const closeSuggestionRef = useRef(closeSuggestion);
    closeSuggestionRef.current = closeSuggestion;

    const suggestion = useMemo(
      () => ({
        char: '@',
        allowSpaces: true,
        allowedPrefixes: null,
        items: () => [] as never[],

        render: () => ({
          onStart: (props: SuggestionProps) => {
            let position: { top: number; left: number } | null = null;
            if (props.clientRect) {
              const rect = props.clientRect();
              if (rect) {
                position = { top: rect.top, left: rect.left };
              }
            }
            suggestionRangeRef.current = props.range;
            setSuggestionState({
              isOpen: true,
              position,
              query: props.query,
              command: props.command as SuggestionState['command'],
            });
          },

          onUpdate: (props: SuggestionProps) => {
            let position: { top: number; left: number } | null = null;
            if (props.clientRect) {
              const rect = props.clientRect();
              if (rect) {
                position = { top: rect.top, left: rect.left };
              }
            }
            suggestionRangeRef.current = props.range;
            setSuggestionState((prev) => ({
              ...prev,
              query: props.query,
              position: position || prev.position,
              command: props.command as SuggestionState['command'],
            }));
          },

          onKeyDown: (props: SuggestionKeyDownProps) => {
            // Let Cmd/Ctrl+Enter pass through so editorProps.handleKeyDown can submit the prompt.
            if (props.event.key === 'Enter' && (props.event.metaKey || props.event.ctrlKey)) {
              return false;
            }
            if (['ArrowUp', 'ArrowDown', 'Enter', 'Tab', 'Escape'].includes(props.event.key)) {
              return true;
            }
            return false;
          },

          onExit: () => {
            closeSuggestionRef.current();
          },
        }),
      }),
      [],
    );

    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          heading: false,
          bulletList: false,
          orderedList: false,
          listItem: false,
          blockquote: false,
          codeBlock: false,
          horizontalRule: false,
          hardBreak: false,
          bold: false,
          italic: false,
          strike: false,
          code: false,
        }),
        VegaMention.configure({
          suggestion,
        }),
        Placeholder.configure({
          placeholder: () => placeholderRef.current,
          emptyEditorClass: vegaEditorPlaceholder,
        }),
      ],
      content: initialContent,
      onUpdate: ({ editor: updatedEditor }) => {
        const doc = updatedEditor.getJSON();
        const plainText = extractPlainText(doc);
        onTextChangeRef.current(plainText);

        const currentKeys = getMentionKeys(doc);
        const storage = getMentionStorage(updatedEditor);
        storage.resources = storage.resources.filter((r) => currentKeys.has(mentionKeyForResource(r)));
      },
      editorProps: {
        attributes: {
          class: vegaEditorInput,
        },
        handleKeyDown: (_view, event) => {
          if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
            event.preventDefault();
            event.stopPropagation();
            onSubmitRef.current();
            return true;
          }
          return false;
        },
      },
      immediatelyRender: false,
      editable: !disabled,
    });

    useEffect(() => {
      if (editor && !editor.isDestroyed) {
        editor.view.dispatch(editor.state.tr);
      }
    }, [editor, placeholder]);

    useEffect(() => {
      if (editor) {
        editor.setEditable(!disabled);
      }
    }, [editor, disabled]);

    // Only store in editor storage -- mention resources are collected at submit time via
    // getMentionResources(). Context pills only show items attached via contextual entry points
    // (e.g. "Ask Vega" on a log or span), not inline @-mentions.
    const handleResourceAttached = useCallback(
      (resource: ResourceOrQuery) => {
        if (editor) {
          getMentionStorage(editor).resources.push(resource);
        }
      },
      [editor],
    );

    // Tiptap's `useEditor` returns null on the first render when immediatelyRender is false,
    // so insertMention() may be called before the editor is mounted (e.g. when
    // VegaPromptComposer's migration effect runs on mount). Queue those insertions and flush
    // them once the editor is ready so no resources are dropped.
    const pendingInsertionsRef = useRef<ResourceOrQuery[]>([]);

    const performInsertion = useCallback(
      (resource: ResourceOrQuery) => {
        if (!editor) {
          return;
        }
        const resourceType = resource.type ?? inferContextType(resource);
        const label = getContextLabel(resource);
        getMentionStorage(editor).resources.push(resource);
        editor
          .chain()
          .focus()
          .insertContent([
            { type: 'mention', attrs: { id: resource.id, label, resourceType } },
            { type: 'text', text: ' ' },
          ])
          .run();
      },
      [editor],
    );

    useEffect(() => {
      if (!editor || pendingInsertionsRef.current.length === 0) {
        return;
      }
      const pending = pendingInsertionsRef.current;
      pendingInsertionsRef.current = [];
      pending.forEach(performInsertion);
    }, [editor, performInsertion]);

    useImperativeHandle(ref, () => ({
      focus: () => editor?.commands.focus('end'),
      clear: () => {
        editor?.commands.clearContent();
        if (editor) {
          getMentionStorage(editor).resources = [];
        }
        pendingInsertionsRef.current = [];
      },
      setContent: (html: string) => {
        if (editor && !editor.isDestroyed) {
          editor.commands.setContent(html);
          editor.commands.focus('end');
        }
      },
      getMentionResources: (): ResourceOrQuery[] => {
        if (!editor) {
          return [];
        }
        const currentKeys = getMentionKeys(editor.getJSON());
        return getMentionStorage(editor).resources.filter((r) => currentKeys.has(mentionKeyForResource(r)));
      },
      insertMention: (resource: ResourceOrQuery) => {
        if (!editor) {
          pendingInsertionsRef.current.push(resource);
          return;
        }
        performInsertion(resource);
      },
    }));

    // User-initiated dismiss (Escape / click-outside): move cursor before the
    // @ trigger so Tiptap's suggestion plugin fires onExit naturally. This
    // ensures key interception (Arrow, Enter, Tab) stops after dismissal.
    const dismissSuggestion = useCallback(() => {
      const range = suggestionRangeRef.current;
      if (editor && range) {
        editor.chain().setTextSelection(range.from).run();
      } else {
        closeSuggestion();
      }
    }, [editor, closeSuggestion]);

    const handleClearQuery = useCallback(() => {
      const range = suggestionRangeRef.current;
      if (!editor || !range) {
        return;
      }
      const { from, to } = range;
      if (to > from + 1) {
        editor
          .chain()
          .focus()
          .deleteRange({ from: from + 1, to })
          .run();
      }
    }, [editor]);

    return (
      <>
        <EditorContent editor={editor} />
        {createPortal(
          <MentionSuggestionPopover
            isOpen={suggestionState.isOpen}
            position={suggestionState.position}
            query={suggestionState.query}
            command={suggestionState.command}
            onClearQuery={handleClearQuery}
            onClose={dismissSuggestion}
            onResourceAttached={handleResourceAttached}
          />,
          document.body,
        )}
      </>
    );
  },
);
